/* eslint-disable @typescript-eslint/no-unused-vars */
import i18nInst from 'src/i18n';
import {
  ActionData,
  ActionEnum,
  ApplyBoundEnum,
  DdaActionKeys,
  DdaNotifyTypeKeys,
  DdaSuspiciousTagKeys,
  EnableDisableEnum,
  ExchangeOnlinePolicyItem,
  MailPolicyNameEnum,
} from 'src/pages/Policy/utils/types';

import { PolicyTypeEnum } from '@Policy/utils/constants';
import { PolicyStore } from '@Policy/store/createStore';
import { policyTypeNameSelector } from '../general/store';

export const ddaApprovedSenderEmailEntriesSelector = (state: PolicyStore) => {
  const template = state.template as ExchangeOnlinePolicyItem;
  const ddaApprovedSenderEmailEntries =
    template.tmDdafilters.ddaApprovedSenderEmailEntries;
  const separator = ';';
  if (ddaApprovedSenderEmailEntries === '') {
    return [];
  }
  const list = ddaApprovedSenderEmailEntries
    .split(separator)
    .filter((item) => item.length > 0)
    .map((item) => ({
      name: item,
      value: item,
    }));
  return list;
};

export const ddaApprovedFileNameEntriesSelector = (state: PolicyStore) => {
  const template = state.template as ExchangeOnlinePolicyItem;
  const ddaApprovedFileNameEntries =
    template.tmDdafilters.ddaApprovedFileNameEntries;
  const separator = '|';
  if (ddaApprovedFileNameEntries === '') {
    return [];
  }
  const list = ddaApprovedFileNameEntries
    .split(separator)
    .filter((item) => item.length > 0)
    .map((item) => ({
      name: item,
      value: item,
    }));
  return list;
};

export const ddaActionDataSelector = (state: PolicyStore) => {
  const template = state.template as ExchangeOnlinePolicyItem;
  const {
    setDdaState,
    config: {
      featureShow: { gmailQtSettingFeatureShow },
    },
  } = state;
  const {
    tmDdafilters: {
      ddaActionHighRisk,
      ddaActionMediumRisk,
      ddaActionLowRisk,
      ddaActionUnknowRisk,
      ddaHighRiskNotifyType,
      ddaMediumRiskNotifyType,
      ddaLowRiskNotifyType,
      ddaUnknowRiskNotifyType,
      ddaSuspiciousTagHighRisk,
      ddaSuspiciousTagMediumRisk,
      ddaSuspiciousTagLowRisk,
      ddaSuspiciousTagUnknowRisk,
      ddaMonitorModeEnabled,
    },
  } = template;
  const { t } = i18nInst;
  const policyType = state.template.policyType;
  const policyTypeName = policyTypeNameSelector(state);
  let actionOptions: ActionEnum[] = [];
  // Exchange, Exchange inline, Gmail inline
  const actionOptionsExchange = [
    ActionEnum.tagSubject,
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.pass,
  ];
  // Gmail
  const actionOptionsGmail = gmailQtSettingFeatureShow
    ? [
        ActionEnum.delete,
        ActionEnum.quarantine,
        ActionEnum.pass,
        ActionEnum.labelEmail,
      ]
    : [ActionEnum.delete, ActionEnum.pass, ActionEnum.labelEmail];
  // Teams, OneDrive, SharePoint, Box, GoogleDrive, Dropbox
  const actionOptionsTeams = [
    ActionEnum.delete,
    ActionEnum.quarantine,
    ActionEnum.pass,
  ];
  // Teams chat
  const actionOptionsTeamsChat = [ActionEnum.block, ActionEnum.pass];
  switch (policyType) {
    case PolicyTypeEnum.Exchange:
    case PolicyTypeEnum.ExchangeInline:
    case PolicyTypeEnum.GmailInline:
      actionOptions = actionOptionsExchange;
      break;
    case PolicyTypeEnum.Gmail:
      actionOptions = actionOptionsGmail;
      break;
    case PolicyTypeEnum.Teams:
    case PolicyTypeEnum.OneDrive:
    case PolicyTypeEnum.SharePoint:
    case PolicyTypeEnum.Box:
    case PolicyTypeEnum.Dropbox:
    case PolicyTypeEnum.GoogleDrive:
      actionOptions = actionOptionsTeams;
      break;
    case PolicyTypeEnum.TeamsChat:
      actionOptions = actionOptionsTeamsChat;
      break;
    default:
      break;
  }
  // Unrated action options
  const actionOptionsUnrated = [
    MailPolicyNameEnum.Exchange,
    MailPolicyNameEnum.ExchangeInlineInbound,
  ].includes(policyTypeName as MailPolicyNameEnum)
    ? [...actionOptions, ActionEnum.moveToJunkEmailFolder]
    : actionOptions;
  const actionData: ActionData[] = [
    {
      threatType: t('policy.threatType.highRisk'),
      handler: setDdaState,
      action: ddaActionHighRisk,
      actionKey: DdaActionKeys.ddaActionHighRisk,
      actions: actionOptions,
      actionTypeDisabled: !!ddaMonitorModeEnabled,
      notifyType: ddaHighRiskNotifyType,
      notifyTypeKey: DdaNotifyTypeKeys.ddaHighRiskNotifyType,
      suspiciousTag: ddaSuspiciousTagHighRisk,
      suspiciousTagKey: DdaSuspiciousTagKeys.ddaSuspiciousTagHighRisk,
    },
    {
      threatType: t('policy.threatType.mediumRisk'),
      handler: setDdaState,
      action: ddaActionMediumRisk,
      actionKey: DdaActionKeys.ddaActionMediumRisk,
      actions: actionOptions,
      actionTypeDisabled: !!ddaMonitorModeEnabled,
      notifyType: ddaMediumRiskNotifyType,
      notifyTypeKey: DdaNotifyTypeKeys.ddaMediumRiskNotifyType,
      suspiciousTag: ddaSuspiciousTagMediumRisk,
      suspiciousTagKey: DdaSuspiciousTagKeys.ddaSuspiciousTagMediumRisk,
    },
    {
      threatType: t('policy.threatType.lowRisk'),
      handler: setDdaState,
      action: ddaActionLowRisk,
      actionKey: DdaActionKeys.ddaActionLowRisk,
      actions: actionOptions,
      actionTypeDisabled: !!ddaMonitorModeEnabled,
      notifyType: ddaLowRiskNotifyType,
      notifyTypeKey: DdaNotifyTypeKeys.ddaLowRiskNotifyType,
      suspiciousTag: ddaSuspiciousTagLowRisk,
      suspiciousTagKey: DdaSuspiciousTagKeys.ddaSuspiciousTagLowRisk,
    },
    {
      threatType: t('policy.threatType.unrate'),
      handler: setDdaState,
      action: ddaActionUnknowRisk,
      actionKey: DdaActionKeys.ddaActionUnknowRisk,
      actions: actionOptionsUnrated,
      actionTypeDisabled: !!ddaMonitorModeEnabled,
      notifyType: ddaUnknowRiskNotifyType,
      notifyTypeKey: DdaNotifyTypeKeys.ddaUnknowRiskNotifyType,
      suspiciousTag: ddaSuspiciousTagUnknowRisk,
      suspiciousTagKey: DdaSuspiciousTagKeys.ddaSuspiciousTagUnknowRisk,
    },
  ];
  return actionData;
};
export const ddaApplyBoundDisabledSelector = (state: PolicyStore) => {
  const template = state.template as ExchangeOnlinePolicyItem;
  const {
    tmDdafilters: { ddaURLEnabled },
    tmVirusscans: { vsApplyBound },
    tmWebthreatprotections: { wtpApplyBound },
  } = template;
  const disabled =
    vsApplyBound === ApplyBoundEnum.incomming ||
    (ddaURLEnabled && wtpApplyBound === ApplyBoundEnum.incomming);
  return disabled;
};

export const ddaTempQuarantineEnabledSelector = (state: PolicyStore) => {
  const template = state.template as ExchangeOnlinePolicyItem;
  const {
    tmDdafilters: { ddaFileTempQuarantineEnabled, ddaURLTempQuarantineEnabled },
  } = template;
  const ddaTempQuarantineEnabled =
    ddaFileTempQuarantineEnabled === 0 && ddaURLTempQuarantineEnabled === 0
      ? 0
      : 1;
  return ddaTempQuarantineEnabled;
};

export const ddaAdvancedOptionConfigSelector = (s: PolicyStore) => {
  const {
    tmDdafilters: { ddaReplacementText },
  } = s.template as ExchangeOnlinePolicyItem;
  const config = {
    replaceFileName: '',
    replaceText: ddaReplacementText,
    replaceTextDefault:
      s.config.defaultValue?.tmDdafilters.ddaReplacementText || '',
  };
  console.log('config', s.config, config);
  return config;
};
